import { CSSProperties, useMemo } from 'react'

import {
  AreaChartSeries,
  BarChartSeries,
  LemonadeAreaChart,
  LemonadeBarChart,
  LemonadeDonutChart,
  LemonadeLineChart,
  LineChartSeries,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableProvider,
  TableRow,
} from '@percent/lemonade'

import { MetricData, ValueType, valueAmount } from '../../types/insights.types'
import { InsightsColourMap, resolveColour } from '../../utils/colourResolver'
import {
  dateTickFormatter,
  formatValueHero,
  tickFormatterFor,
  valueFormatterFor,
} from '../../utils/formatValue'
import {
  CATEGORY_KEY,
  TOTAL_KEY,
  hasMixedUnits,
  isDualAxis,
  orderByKeys,
  rowAxisLabel,
  rowsBarData,
  rowsDonutData,
  rowTotalSample,
  sampleValue,
  SeriesAxis,
  seriesAxisAssignment,
  seriesSampleValue,
  SeriesView,
  seriesTotalsDonutData,
  seriesViews,
  timeseriesChartData,
} from '../../utils/metricView'
import { DEFAULT_CHART_HEIGHT } from '../constants'

// A stacked bar's series set is kept *stable across filters* — it's the domain's
// full declared taxonomy (`seriesOrder`), enriched with the live label/colour for
// whichever series the current response includes, plus any present series outside
// the taxonomy. A series that's absent for the active filter stays mounted at zero
// width: it renders nothing (zero-width rect), but because its `<Bar>` never
// unmounts, a series that later *gains* a value tweens in place instead of
// mounting and sliding the whole stack to the end (NEX-685). The set is also
// always >1, so the stack uses the outer-end rounding rather than a single bar's
// all-corner radius. Memoised on a content signature so the array ref only
// changes when the set/order/colours actually change.
const useStableSeries = (
  data: MetricData,
  colourMap: InsightsColourMap,
  seriesOrder: string[]
): SeriesView[] => {
  const present = data.type === 'seriesRows' ? seriesViews(data, colourMap) : []
  const presentByName = new Map(present.map((view) => [view.name, view]))

  const taxonomy = seriesOrder.map(
    (key, index): SeriesView =>
      presentByName.get(key) ?? {
        name: key,
        label: key,
        color: resolveColour(key, index, colourMap),
      }
  )
  const extras = present.filter((view) => !seriesOrder.includes(view.name))
  const ordered = orderByKeys(
    [...taxonomy, ...extras],
    seriesOrder,
    (view) => view.name
  )
  // The label belongs in the signature: a series the response hasn't declared yet
  // is stubbed with its key as the label, and its name/colour are the same either
  // way (both come from the colour map), so leaving the label out pinned that stub
  // for the life of the card — the legend, read straight off the data, would name
  // the series while the bars and their tooltip still showed the raw key.
  const signature = ordered
    .map((view) => `${view.name}:${view.label}:${view.color}`)
    .join('|')

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ordered, [signature])
}

/** Host-supplied copy for the table body's generic column headers (the series/label
 * headers come from the data). Each defaults to English when omitted, matching the
 * lib's convention of injecting user-facing text (errorLabel, emptyState, …). */
export type TableLabels = { value?: string; total?: string; share?: string }

export type ChartBodyProps = {
  data: MetricData
  colourMap: InsightsColourMap
  /** Canonical series order shared across the domain's charts. */
  seriesOrder?: string[]
  height?: number
  orientation?: 'vertical' | 'horizontal'
  wrapXAxisLabels?: boolean
  yAxisWidth?: number
  innerRadiusRatio?: number
  /** Line / area / combo chart: series keys to plot against the right Y-axis. When
   * omitted, the chart splits by unit — see `assignSeriesAxes`. */
  rightAxisSeries?: string[]
  /** Table body: translated headers for the generic Value/Total/Share columns. */
  tableLabels?: TableLabels
  /** BCP 47 locale for the axis ticks and tooltip values; defaults to `en-US`. */
  locale?: string
}

/**
 * The name of a series on `axis` that carries a value, so its unit can drive the
 * axis' tick formatter. Series are grouped onto axes by unit, so any valued series
 * on the axis gives the same answer — but a *valueless* one gives none, which is
 * why the first series on the axis isn't good enough.
 */
const valuedSeriesOn = (
  series: readonly { name: string; axis?: SeriesAxis }[],
  sampleByName: Record<string, ValueType | undefined>,
  axis: SeriesAxis
): string | undefined =>
  series.find((s) => s.axis === axis && sampleByName[s.name] !== undefined)
    ?.name

/**
 * The label to caption an axis with: the name of the *only* series measured against
 * it. Axes are captioned only on a two-axis chart, where the reader has to tell
 * which side a series belongs to; with several series sharing an axis the legend's
 * colours already carry that, and no single label would be honest.
 */
const soleSeriesLabelOn = (
  series: readonly { name: string; label?: string }[],
  axisByName: Record<string, SeriesAxis>,
  axis: SeriesAxis
): string | undefined => {
  const onAxis = series.filter((s) => axisByName[s.name] === axis)

  return onAxis.length === 1 ? onAxis[0].label : undefined
}

// Focused presentational bodies. Transforms are memoised so the series/data
// identities are stable across re-renders (e.g. while a new filter combination
// loads behind keepPreviousData) — keeping the bar's rounded end-cap pinned to
// the top-of-stack segment instead of re-animating onto the wrong one.

type BarConfig = {
  chartData: Record<string, unknown>[]
  series: BarChartSeries[]
  /** Side-by-side bars rather than one stack — set when the series' units differ. */
  grouped: boolean
  leftSample: ValueType | undefined
  rightSample: ValueType | undefined
  leftAxisLabel?: string
  rightAxisLabel?: string
  /** Whether the per-row total is a real figure worth putting in the tooltip. */
  showTotal: boolean
}

/**
 * Stacked bar for a `seriesRows` metric; single-series bar for a flat `rows` metric.
 *
 * A stack only means something when its segments share a unit, so a breakdown whose
 * series carry different units (a count next to a money amount) renders as grouped
 * bars split across two value axes instead — each axis scaled and formatted by its
 * own unit — and drops the per-row total from the tooltip. Same-unit series keep
 * stacking exactly as before.
 */
export function BarChartBody({
  data,
  colourMap,
  seriesOrder = [],
  height = DEFAULT_CHART_HEIGHT,
  orientation = 'vertical',
  wrapXAxisLabels,
  yAxisWidth,
  locale,
}: Readonly<ChartBodyProps>) {
  const series = useStableSeries(data, colourMap, seriesOrder)

  const config = useMemo((): BarConfig | null => {
    if (data.type === 'seriesRows') {
      const assignment = seriesAxisAssignment(
        data,
        series.map((view) => view.name)
      )
      const { axisByName } = assignment
      const grouped = isDualAxis(assignment)
      // Series whose unit had no axis to go on aren't plotted; the legend still
      // names them (see `axisAwareLegendItems`). A stack additionally keeps absent
      // taxonomy series mounted at zero so one that later gains a value tweens in
      // place (NEX-685) — grouped bars give every series its own slot in the
      // category band, where a valueless one would only squeeze the visible bars.
      const plotted = series.filter(
        (view) =>
          view.name in axisByName &&
          (!grouped || seriesSampleValue(data, view.name) !== undefined)
      )

      const plottedNames = new Set(plotted.map((view) => view.name))

      // One record per row (response order). Each carries a value for every
      // rendered series — 0 when that series is absent for the row.
      const rows = data.rows.map((row) => {
        const record: Record<string, unknown> = {
          [CATEGORY_KEY]: row.label,
          [TOTAL_KEY]: valueAmount(row.total),
        }

        for (const name of plottedNames) record[name] = 0
        for (const value of row.values) {
          if (plottedNames.has(value.series))
            record[value.series] = valueAmount(value.value)
        }

        return record
      })

      // The axis' unit comes from a series that actually carries a value — see
      // {@link valuedSeriesOn}.
      const sampleOn = (axis: SeriesAxis) => {
        for (const view of plotted) {
          if (axisByName[view.name] !== axis) continue

          const sample = seriesSampleValue(data, view.name)

          if (sample !== undefined) return sample
        }

        return undefined
      }

      return {
        chartData: rows,
        series: plotted.map((view) => ({
          ...view,
          axis: axisByName[view.name],
        })),
        grouped,
        leftSample: sampleOn('left'),
        rightSample: sampleOn('right'),
        leftAxisLabel: grouped
          ? soleSeriesLabelOn(plotted, axisByName, 'left')
          : undefined,
        rightAxisLabel: grouped
          ? soleSeriesLabelOn(plotted, axisByName, 'right')
          : undefined,
        showTotal: !hasMixedUnits(
          data,
          plotted.map((view) => view.name)
        ),
      }
    }

    if (data.type === 'rows') {
      return {
        chartData: rowsBarData(data),
        series: [
          { name: 'value', color: resolveColour('value', 0, colourMap) },
        ],
        grouped: false,
        leftSample: sampleValue(data),
        rightSample: undefined,
        // A flat breakdown's records carry no row total to show.
        showTotal: false,
      }
    }

    return null
  }, [data, colourMap, series])

  if (!config) return null

  return (
    <LemonadeBarChart
      data={config.chartData}
      dataKey={CATEGORY_KEY}
      series={config.series}
      orientation={orientation}
      grouped={config.grouped}
      h={height}
      tickFormatter={tickFormatterFor(config.leftSample, locale)}
      rightTickFormatter={tickFormatterFor(config.rightSample, locale)}
      leftAxisLabel={config.leftAxisLabel}
      rightAxisLabel={config.rightAxisLabel}
      categoryAxisLabel={rowAxisLabel(data)}
      totalKey={config.showTotal ? TOTAL_KEY : undefined}
      wrapXAxisLabels={orientation === 'vertical' ? wrapXAxisLabels : undefined}
      yAxisProps={
        orientation === 'horizontal' && yAxisWidth != null
          ? { width: yAxisWidth }
          : undefined
      }
      showGrid
    />
  )
}

/** Donut of a flat `rows` breakdown, or a `seriesRows` metric's row totals. */
export function DonutChartBody({
  data,
  colourMap,
  height = DEFAULT_CHART_HEIGHT,
  innerRadiusRatio,
  locale,
}: Readonly<ChartBodyProps>) {
  const donut = useMemo(() => {
    if (data.type === 'rows') return rowsDonutData(data, colourMap)
    if (data.type === 'seriesRows')
      return seriesTotalsDonutData(data, colourMap)

    return []
  }, [data, colourMap])

  if (donut.length === 0) return null

  return (
    <LemonadeDonutChart
      data={donut}
      nameKey="label"
      valueKey="amount"
      colors={donut.map((datum) => datum.color)}
      tickFormatter={tickFormatterFor(sampleValue(data), locale)}
      innerRadiusRatio={innerRadiusRatio}
      h={height}
    />
  )
}

type TimeseriesConfig = {
  chartData: Record<string, unknown>[]
  series: LineChartSeries[]
  leftSample: ValueType | undefined
  rightSample: ValueType | undefined
  sampleByName: Record<string, ValueType | undefined>
  /** Unit of the per-bucket total baked into each record under {@link TOTAL_KEY}. */
  totalSample: ValueType | undefined
  /** Whether the per-bucket total is a real figure worth putting in the tooltip. */
  showTotal: boolean
  leftAxisLabel?: string
  rightAxisLabel?: string
}

/**
 * Formats a tooltip value in its own series' unit. The bucket total is dispatched
 * under {@link TOTAL_KEY} rather than a series name, so it formats as a total —
 * money stays money even on a chart whose first series is a count.
 */
const timeseriesValueFormatter =
  (config: TimeseriesConfig, locale?: string) =>
  (value: number, name: string) =>
    valueFormatterFor(
      name === TOTAL_KEY ? config.totalSample : config.sampleByName[name],
      locale
    )(value)

/**
 * Shared config for the timeseries line/area/combo bodies. Series are split across
 * two Y-axes by unit — e.g. a count on the left, a money amount on the right — each
 * axis scaled and formatted by its own unit, and captioned with its series' label
 * when it hosts exactly one. A series whose unit has no axis left (three-plus units
 * in one breakdown) is dropped from the plot rather than mis-scaled; the legend
 * still names it. Handles a multi-series `seriesRows` timeseries and a flat
 * single-series `rows` one. Returns null for a non-timeseries shape.
 */
function buildTimeseriesConfig(
  data: MetricData,
  colourMap: InsightsColourMap,
  seriesOrder: string[],
  rightAxisSeries: string[] | undefined
): TimeseriesConfig | null {
  if (data.type === 'rows') {
    // Flat timeseries: a single left-axis series.
    const sample = data.rows[0]?.value

    return {
      chartData: timeseriesChartData(data),
      series: [
        {
          name: 'value',
          color: resolveColour('value', 0, colourMap),
          axis: 'left',
        },
      ],
      leftSample: sample,
      rightSample: undefined,
      sampleByName: { value: sample },
      totalSample: undefined,
      showTotal: false,
    }
  }

  if (data.type !== 'seriesRows') return null

  const views = orderByKeys(
    seriesViews(data, colourMap),
    seriesOrder,
    (view) => view.name
  )
  const sampleByName: Record<string, ValueType | undefined> = {}

  views.forEach((view) => {
    sampleByName[view.name] = seriesSampleValue(data, view.name)
  })

  const { axisByName } = seriesAxisAssignment(
    data,
    views.map((view) => view.name),
    rightAxisSeries
  )
  const series: LineChartSeries[] = views
    .filter((view) => view.name in axisByName)
    .map((view) => ({
      name: view.name,
      label: view.label,
      color: view.color,
      axis: axisByName[view.name],
    }))
  // The axis' unit comes from a series that actually carries a value. Taking the
  // first series on the axis regardless would hand a *valueless* one the job (the
  // wire format allows sparse rows, so a declared series can be empty throughout),
  // leaving the formatter with no unit — money would then print as raw minor units,
  // 100x out and wide enough to clip the axis.
  const leftName = valuedSeriesOn(series, sampleByName, 'left')
  const rightName = valuedSeriesOn(series, sampleByName, 'right')

  return {
    chartData: timeseriesChartData(data),
    series,
    leftSample: leftName ? sampleByName[leftName] : undefined,
    rightSample: rightName ? sampleByName[rightName] : undefined,
    sampleByName,
    totalSample: rowTotalSample(data),
    showTotal: !hasMixedUnits(
      data,
      series.map((s) => s.name)
    ),
    leftAxisLabel: rightName
      ? soleSeriesLabelOn(series, axisByName, 'left')
      : undefined,
    rightAxisLabel: rightName
      ? soleSeriesLabelOn(series, axisByName, 'right')
      : undefined,
  }
}

/** A timeseries line chart. See {@link buildTimeseriesConfig} for the axis handling. */
export function LineChartBody({
  data,
  colourMap,
  seriesOrder = [],
  height = DEFAULT_CHART_HEIGHT,
  rightAxisSeries,
  locale,
}: Readonly<ChartBodyProps>) {
  const config = useMemo(
    () => buildTimeseriesConfig(data, colourMap, seriesOrder, rightAxisSeries),
    [data, colourMap, seriesOrder, rightAxisSeries]
  )

  if (!config || config.chartData.length === 0) return null

  const xValues = config.chartData.map((row) => String(row[CATEGORY_KEY]))

  return (
    <LemonadeLineChart
      data={config.chartData}
      dataKey={CATEGORY_KEY}
      series={config.series}
      h={height}
      showGrid
      xTickFormatter={dateTickFormatter(xValues)}
      leftTickFormatter={tickFormatterFor(config.leftSample, locale)}
      rightTickFormatter={tickFormatterFor(config.rightSample, locale)}
      leftAxisLabel={config.leftAxisLabel}
      rightAxisLabel={config.rightAxisLabel}
      categoryAxisLabel={rowAxisLabel(data)}
      valueFormatter={timeseriesValueFormatter(config, locale)}
      totalKey={config.showTotal ? TOTAL_KEY : undefined}
    />
  )
}

export type AreaChartBodyProps = Readonly<
  ChartBodyProps & {
    /** Cumulate the series into stacked bands instead of overlaying them. */
    stacked?: boolean
  }
>

/**
 * A timeseries area chart. `stacked` cumulates the series into bands (useful for
 * a part-to-whole-over-time breakdown); overlaid otherwise. Stacking is only
 * meaningful on a single shared scale, so a stacked area pins every series to the
 * left axis.
 */
export function AreaChartBody({
  data,
  colourMap,
  seriesOrder = [],
  height = DEFAULT_CHART_HEIGHT,
  rightAxisSeries,
  locale,
  stacked = false,
}: AreaChartBodyProps) {
  const config = useMemo(
    () =>
      buildTimeseriesConfig(
        data,
        colourMap,
        seriesOrder,
        stacked ? undefined : rightAxisSeries
      ),
    [data, colourMap, seriesOrder, rightAxisSeries, stacked]
  )

  if (!config || config.chartData.length === 0) return null

  const xValues = config.chartData.map((row) => String(row[CATEGORY_KEY]))
  const series: AreaChartSeries[] = stacked
    ? config.series.map((s) => ({ ...s, axis: 'left' as const }))
    : config.series

  return (
    <LemonadeAreaChart
      data={config.chartData}
      dataKey={CATEGORY_KEY}
      series={series}
      stacked={stacked}
      h={height}
      showGrid
      xTickFormatter={dateTickFormatter(xValues)}
      leftTickFormatter={tickFormatterFor(config.leftSample, locale)}
      rightTickFormatter={tickFormatterFor(config.rightSample, locale)}
      leftAxisLabel={stacked ? undefined : config.leftAxisLabel}
      rightAxisLabel={stacked ? undefined : config.rightAxisLabel}
      categoryAxisLabel={rowAxisLabel(data)}
      valueFormatter={timeseriesValueFormatter(config, locale)}
      totalKey={config.showTotal ? TOTAL_KEY : undefined}
    />
  )
}

// A cell of the derived table: its rendered text plus the alignment it shares with
// its header — a right-aligned numeric magnitude (value/total/series/share) or the
// left-aligned label.
type TableChartCell = { key: string; content: string; style?: CSSProperties }
type TableChartConfig = { columns: TableColumn[]; rows: TableChartCell[][] }

const EMPTY_SERIES_CELL = '—'

// Right-aligned so magnitudes line up on the right the way currency is
// conventionally tabulated (matching InsightsDataset). Header and cells share this
// one style so they can't drift apart.
const RIGHT_ALIGNED: CSSProperties = { textAlign: 'right' }

const numericColumn = (id: string, content: string): TableColumn => ({
  id,
  content,
  props: RIGHT_ALIGNED,
})

const numericCell = (key: string, content: string): TableChartCell => ({
  key,
  content,
  style: RIGHT_ALIGNED,
})

const labelCell = (label: string): TableChartCell => ({
  key: 'label',
  content: label,
})

/**
 * Renders a breakdown as a plain table — the body for the backend's `table`
 * suggestion. A flat `rows` breakdown becomes a label + `Value` table (plus a
 * `Share` column for `percentageRows`); a `seriesRows` breakdown becomes a label
 * column, one column per series, and a `Total`. The key column is headed by the
 * breakdown's row axis ("Country"), and left blank when the response doesn't name
 * one — the rows' own labels then carry the meaning, as they did before the axis
 * existed. Values format with the default locale via {@link formatValueHero},
 * exactly like the other chart bodies. Returns `null` for a scalar (defensive —
 * the spec never selects this body for a scalar).
 */
export function TableChartBody({
  data,
  tableLabels,
  locale,
}: Readonly<ChartBodyProps>) {
  const config = useMemo<TableChartConfig | null>(() => {
    const valueLabel = tableLabels?.value ?? 'Value'
    const totalLabel = tableLabels?.total ?? 'Total'
    const shareLabel = tableLabels?.share ?? 'Share'
    // Already localised by the backend, so it needs no `tableLabels` counterpart.
    const labelHeader = rowAxisLabel(data) ?? ''

    if (data.type === 'rows') {
      const isPercentage = data.structure === 'percentageRows'
      const columns: TableColumn[] = [
        { id: 'label', content: labelHeader },
        numericColumn('value', valueLabel),
        ...(isPercentage ? [numericColumn('share', shareLabel)] : []),
      ]
      const rows = data.rows.map((row): TableChartCell[] => [
        labelCell(row.label),
        numericCell('value', formatValueHero(row.value, locale)),
        ...(isPercentage
          ? [numericCell('share', `${Math.round((row.percent ?? 0) * 100)}%`)]
          : []),
      ])

      return { columns, rows }
    }

    if (data.type === 'seriesRows') {
      // A total across series of differing units is meaningless — money added to a
      // count — so the column is dropped rather than tabulated, the same rule the
      // chart bodies apply to the tooltip total.
      const showTotal = !hasMixedUnits(
        data,
        data.series.map((series) => series.key)
      )
      const columns: TableColumn[] = [
        { id: 'label', content: labelHeader },
        ...data.series.map((series) => numericColumn(series.key, series.label)),
        ...(showTotal ? [numericColumn('total', totalLabel)] : []),
      ]
      const rows = data.rows.map((row): TableChartCell[] => [
        labelCell(row.label),
        ...data.series.map((series) => {
          const match = row.values.find((value) => value.series === series.key)

          return numericCell(
            series.key,
            match ? formatValueHero(match.value, locale) : EMPTY_SERIES_CELL
          )
        }),
        ...(showTotal
          ? [numericCell('total', formatValueHero(row.total, locale))]
          : []),
      ])

      return { columns, rows }
    }

    // Scalar — the spec won't route a scalar here, but stay defensive.
    return null
    // Depend on the resolved label strings, not the `tableLabels` object, so an
    // inline `{…}` from the host doesn't churn the memo every render.
  }, [data, locale, tableLabels?.value, tableLabels?.total, tableLabels?.share])

  if (!config) return null

  return (
    <TableProvider
      columns={config.columns}
      totalResults={config.rows.length}
      isLoading={false}
      illustration="no-results"
      emptyTableTitle=""
    >
      <Table>
        <TableBody>
          {config.rows.map((cells, rowIndex) => (
            <TableRow key={`${rowIndex}-${cells[0].content}`}>
              {cells.map((cell) => (
                <TableCell key={cell.key} inlineStyle={cell.style}>
                  {cell.content}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableProvider>
  )
}
